// web-commands.ts — the PER-APP half of the web shape: maps solo_habit_tracker's Tauri data
// commands (normally served by lib.rs/db.rs over native SQLite) to the shared browser WebStore.
// Plain CRUD is a few lines each. The one derived command (signals_for_analysis) is the app's
// "paid draw" computed in db.rs (build_headline_facts et al.) — on web it returns a minimal shell
// for now; a faithful web build reimplements that signal logic in JS (the known per-app wrinkle).

import type { CommandHandler, Row, WebStore } from "./web-runtime";
import { computeSignalsWeb } from "./signals-web";
import type { Habit } from "./store";
import { browserSearch, browserFetch } from "./web-search-browser";

const HABITS = "habits";
const ENTRIES = "entries";
const REASONS = "miss_reasons";

function uuid(): string {
  // crypto.randomUUID is available in every modern browser.
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}
function nowIso(): string {
  return new Date().toISOString();
}
function pad(n: number): string {
  return n.toString().padStart(2, "0");
}
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(iso: string, delta: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}
const day10 = (v: unknown): string => String(v ?? "").slice(0, 10);
function validCadence(c: string): boolean {
  return c === "daily" || c === "weekly" || c === "adhoc";
}

export function makeWebCommands(db: WebStore): Record<string, CommandHandler> {
  // Habit rows carry two computed fields (entries_30d, last_logged_on) — mirror the native
  // SELECT_HABIT sub-selects so the dashboard list shows correct counts on web.
  const hydrateHabit = (h: Row): Row => {
    const mine = db.all(ENTRIES).filter((e) => String((e as { habit_id?: unknown }).habit_id) === h.id);
    const cutoff = addDays(today(), -29);
    const entries_30d = mine.filter((e) => day10((e as { logged_on?: unknown }).logged_on) >= cutoff).length;
    const last_logged_on = mine.length
      ? mine
          .map((e) => day10((e as { logged_on?: unknown }).logged_on))
          .reduce((a, b) => (b > a ? b : a))
      : null;
    return { ...h, entries_30d, last_logged_on };
  };

  return {

    // ── WEB-SURFACE RETRIEVAL (Pattern 7, web half) ─────────────────────────────
    // On native, `web_search`/`web_fetch` are Rust commands browsing on the DEVICE. In a browser
    // there is no Rust, and without these the chat's `maybeWebContext` silently returns nothing —
    // the customer gets an ungrounded answer where the phone would have cited sources.
    // Here the BROWSER does it, on the customer's own IP: Tier 1 sources it may read directly
    // (no middleman), then a keyless reader for the open web. Oriora runs no search.
    web_search: async (args: Record<string, unknown>) =>
      await browserSearch(String(args.query ?? ""), typeof args.limit === "number" ? args.limit : 5),
    web_fetch: async (args: Record<string, unknown>) =>
      await browserFetch(String(args.url ?? ""), typeof args.maxChars === "number" ? args.maxChars : 6000),

    // ── Habits ──
    // ACTIVE only — mirrors db.rs's `WHERE archived_at = ''`. Archived habits keep every entry.
    list_habits: () =>
      db
        .all(HABITS)
        .filter((h) => String((h as { archived_at?: unknown }).archived_at ?? "") === "")
        .sort((a, b) => String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")))
        .map(hydrateHabit),
    get_habit: ({ id }) => {
      const h = db.get(HABITS, String(id));
      return h ? hydrateHabit(h) : null;
    },
    create_habit: ({ input }) => {
      const i = (input ?? {}) as Record<string, unknown>;
      const name = String(i.name ?? "").trim();
      const cadenceRaw = String(i.cadence_target ?? "daily");
      const cadence = validCadence(cadenceRaw) ? cadenceRaw : "daily";
      const color = String(i.color ?? "").trim() || "#D6336C";
      const row: Row = {
        id: uuid(),
        name,
        cadence_target: cadence,
        // mirrors db.rs: schedule derives from cadence when not given, so web and native agree
        schedule: String(i.schedule ?? (cadence === "weekly" ? "count:1" : cadence === "adhoc" ? "adhoc" : "daily")),
        polarity: i.polarity === "break" ? "break" : "build",
        archived_at: "",
        color,
        created_at: nowIso(),
      };
      db.insert(HABITS, row);
      return hydrateHabit(row);
    },
    update_habit: ({ id, patch }) => {
      const p = (patch ?? {}) as Record<string, unknown>;
      const clean: Record<string, unknown> = {};
      if (typeof p.name === "string" && p.name.trim() !== "") clean.name = p.name.trim();
      if (typeof p.cadence_target === "string" && validCadence(p.cadence_target)) clean.cadence_target = p.cadence_target;
      if (typeof p.schedule === "string") clean.schedule = p.schedule;
      if (p.polarity === "break" || p.polarity === "build") clean.polarity = p.polarity;
      if (typeof p.color === "string" && p.color.trim() !== "") clean.color = p.color;
      db.update(HABITS, String(id), clean);
      return null;
    },
    delete_habit: ({ id }) => {
      // ON DELETE CASCADE on native — remove the habit's entries too.
      const hid = String(id);
      for (const e of db.all(ENTRIES).filter((e) => String((e as { habit_id?: unknown }).habit_id) === hid)) {
        db.remove(ENTRIES, e.id);
      }
      db.remove(HABITS, hid);
      return null;
    },

    // ── Entries (check-ins) ──
    list_entries: ({ habitId, limit }) => {
      const rows = db
        .all(ENTRIES)
        .filter((e) => String((e as { habit_id?: unknown }).habit_id) === String(habitId))
        .sort((a, b) => {
          const d = day10((b as { logged_on?: unknown }).logged_on).localeCompare(
            day10((a as { logged_on?: unknown }).logged_on),
          );
          return d !== 0 ? d : String(b.created_at ?? "").localeCompare(String(a.created_at ?? ""));
        });
      const lim = typeof limit === "number" ? limit : 400;
      return rows.slice(0, lim);
    },
    // WHY a day was missed — its own record, never an entry (an entry would count as a completion
    // in every streak function). One per habit per day; saying it again is a correction.
    // Archive keeps every entry — that is the whole reason it exists instead of delete.
    set_archived: ({ id, archived }) => {
      db.update(HABITS, String(id), { archived_at: archived ? nowIso() : "" });
      return db.get(HABITS, String(id));
    },
    list_archived: () =>
      db.all(HABITS)
        .filter((h) => String((h as { archived_at?: unknown }).archived_at ?? "") !== "")
        .sort((a, b) => String((b as { archived_at?: unknown }).archived_at).localeCompare(String((a as { archived_at?: unknown }).archived_at))),
    set_miss_reason: ({ habitId, onDay, reason }) => {
      const hid = String(habitId);
      const day = day10(onDay);
      const text = String(reason ?? "").trim();
      const existing = db.all(REASONS).find(
        (r) => String((r as { habit_id?: unknown }).habit_id) === hid && day10((r as { on_day?: unknown }).on_day) === day);
      if (existing) {
        db.update(REASONS, existing.id, { reason: text, created_at: nowIso() });
        return db.get(REASONS, existing.id);
      }
      const row: Row = { id: uuid(), habit_id: hid, on_day: day, reason: text, created_at: nowIso() };
      db.insert(REASONS, row);
      return row;
    },
    list_miss_reasons: ({ limit }) => {
      const rows = db.all(REASONS)
        .slice()
        .sort((a, b) => day10((b as { on_day?: unknown }).on_day).localeCompare(day10((a as { on_day?: unknown }).on_day)));
      return rows.slice(0, typeof limit === "number" ? limit : 400);
    },
    // Log (or amend) a check-in. UNIQUE(habit_id, logged_on) on native → upsert: a same-day
    // re-check updates the note rather than duplicating. Real CRUD (plain data), not shelled.
    log_entry: ({ habitId, loggedOn, note, dayState, photoRef }) => {
      const hid = String(habitId);
      const day = day10(loggedOn);
      const cleanNote = typeof note === "string" && note.trim() !== "" ? note.trim() : null;
      // Only the two RECORDED states exist; anything else is "done" (mirrors db.rs).
      const st = dayState === "skipped" ? "skipped" : "done";
      // photo_ref is TRISTATE, exactly like db.rs::log_entry:
      //   null/undefined → keep any existing photo · "" → clear it · data-URL → set it.
      const photoGiven = typeof photoRef === "string";
      const existing = db
        .all(ENTRIES)
        .find((e) => String((e as { habit_id?: unknown }).habit_id) === hid && day10((e as { logged_on?: unknown }).logged_on) === day);
      if (existing) {
        // COALESCE(excluded.note, entries.note): keep the old note when the new one is null.
        db.update(ENTRIES, existing.id, {
          ...(cleanNote !== null ? { note: cleanNote } : {}),
          state: st,
          ...(photoGiven ? { photo_ref: String(photoRef) } : {}),
        });
        return db.get(ENTRIES, existing.id);
      }
      const row: Row = {
        id: uuid(),
        habit_id: hid,
        logged_on: day,
        note: cleanNote,
        created_at: nowIso(),
        state: st,
        // NEVER leave photo_ref undefined on a new row — the web build reads it directly.
        photo_ref: photoGiven ? String(photoRef) : "",
      };
      return db.insert(ENTRIES, row);
    },
    delete_entry: ({ id }) => {
      db.remove(ENTRIES, String(id));
      return null;
    },
    logged_dates: ({ habitId }) =>
      db
        .all(ENTRIES)
        .filter((e) => String((e as { habit_id?: unknown }).habit_id) === String(habitId))
        .map((e) => day10((e as { logged_on?: unknown }).logged_on))
        .sort((a, b) => a.localeCompare(b)),

    // Legacy raw-dates payload (per-habit { name, cadence_target, logged_dates[] }). Plain
    // aggregation — implemented for real.
    habits_for_analysis: () =>
      db
        .all(HABITS)
        .sort((a, b) => String(a.created_at ?? "").localeCompare(String(b.created_at ?? "")))
        .map((h) => ({
          name: String(h.name ?? ""),
          cadence_target: String(h.cadence_target ?? "daily"),
          logged_dates: db
            .all(ENTRIES)
            .filter((e) => String((e as { habit_id?: unknown }).habit_id) === h.id)
            .map((e) => day10((e as { logged_on?: unknown }).logged_on))
            .sort((a, b) => a.localeCompare(b)),
        })),

    // Derived signals — Rust in db.rs::signals_for_analysis, faithfully ported in signals-web.ts.
    // This used to be a valid-but-EMPTY shell, so on web the billed AI analysis was handed a
    // tracker with no habits in it.
    signals_for_analysis: ({ today: forDate }) => {
      const day = (v: unknown) => String(v ?? "").slice(0, 10);
      const entries = db.all(ENTRIES) as Row[];
      const input = (db.all(HABITS) as unknown as Habit[]).map((h) => ({
        habit: h,
        dates: entries
          .filter((e) => String((e as { habit_id?: string }).habit_id ?? "") === h.id)
          .map((e) => day((e as { logged_on?: unknown }).logged_on))
          .filter(Boolean),
      }));
      // Tally the reasons the same way db.rs does, so both shapes hand the model the same facts.
      const tally = new Map<string, { habit: string; reason: string; count: number }>();
      const nameById = new Map(db.all(HABITS).map((h) => [String(h.id), String((h as { name?: unknown }).name ?? "")]));
      for (const r of db.all(REASONS)) {
        const reason = String((r as { reason?: unknown }).reason ?? "");
        if (reason === "-" || reason === "\u2014" || !reason) continue;   // a decline is not a reason
        const habit = nameById.get(String((r as { habit_id?: unknown }).habit_id)) ?? "";
        const key = `${habit}\u0000${reason}`;
        const cur = tally.get(key);
        if (cur) cur.count++; else tally.set(key, { habit, reason, count: 1 });
      }
      const totals = new Map<string, number>();
      for (const t of tally.values()) totals.set(t.habit, (totals.get(t.habit) ?? 0) + t.count);
      const reasons = [...tally.values()]
        .sort((a, b) => b.count - a.count)
        .map((t) => ({ ...t, of_answered: totals.get(t.habit) ?? 0 }));
      return computeSignalsWeb(input, String(forDate ?? today()), reasons);
    },
  };
}
